/**
 * useScheduleDraftEditorSideEffects — ScheduleDraft 工作台跨层副作用编排层。
 *
 * 本模块从 Context 中抽离 mutation 成功回调、Timetable Tab 同步和延迟选中等
 * 横切逻辑，让 Provider 保持薄组合层。它只桥接 UI state、editor data 与
 * mutation 生命周期，不发起请求或构建视图模型。
 *
 * 设计文档 → docs/20_workspace_ux/28_frontend_code_map_and_data_flow.md
 * 骨架关联 → context/ScheduleDraftEditorWorkspaceContext.jsx (组合层), hooks/useScheduleDraftEditorUiState.js (UI 状态), hooks/useScheduleDraftEditorMutations.js (写操作回调)
 */

import { useEffect } from "react";

import { DRAFT_CURRENT_TRACK_KEY, buildScheduleTrackKey } from "../lib/trackUtils";

const EMPTY_CANVAS_ITEMS = [];
const EMPTY_SELECTABLE_FLIGHT_ITEM_IDS = new Set();

/**
 * Wires mutation-success callbacks to UI state operations.
 *
 * These remain as a Context-level concern because they bridge between
 * mutation hooks (D5, locked) and UI state (useScheduleDraftEditorUiState).
 * Consumers of the context receive the pre-wired mutations object.
 *
 * @param {Object} ui - UI state actions from useScheduleDraftEditorUiState
 * @returns {Object} Callbacks for useScheduleDraftEditorMutations
 */
export function buildWiringCallbacks(ui) {
    return {
        onAddFlightSuccess: (flight, variables = {}) => {
            if (!variables?._keep_add_flight_open) {
                ui.closeAddFlightDialog();
                ui.setPendingFlightSelectionId(flight.id);
            }
        },
        onDeleteFlightSuccess: () => {
            ui.setSelectedItemId(null);
        },
        onRestoreTrackSuccess: () => {
            ui.setSelectedItemId(null);
        },
        onDeleteTrackSuccess: () => {
            ui.setSelectedItemId(null);
            ui.clearTimelineFocusWindow?.();
            ui.clearSelectedCells?.();
        },
        onTrackDeleted: (trackId) => {
            if (!trackId) {
                return;
            }
            const trackKey = buildScheduleTrackKey(trackId);
            ui.removeReferenceTrack?.(trackKey);
            if (ui.timetableTab === trackKey) {
                ui.setTimetableTab(DRAFT_CURRENT_TRACK_KEY);
            }
        },
        onAttachReferenceTrackSuccess: (track) => {
            ui.addReferenceTrack(track.id);
            ui.setTimetableTab(buildScheduleTrackKey(track.id));
        },
        onImportSourceFlightsSuccess: (flights, meta = {}) => {
            ui.closeSourceTrackDialog();
            const firstFlight = Array.isArray(flights) ? flights[0] : null;
            const targetTrackId = firstFlight?.track_id || meta.targetTrackId;
            ui.setTimetableTab(targetTrackId ? buildScheduleTrackKey(targetTrackId) : DRAFT_CURRENT_TRACK_KEY);
            if (firstFlight?.id) {
                ui.setPendingFlightSelectionId(firstFlight.id);
            }
        },
        onImportTableFlightsSuccess: (flights, meta = {}) => {
            ui.closeTableImportDialog();
            const firstFlight = Array.isArray(flights) ? flights[0] : null;
            const targetTrackId = firstFlight?.track_id || meta.targetTrackId;
            ui.setTimetableTab(targetTrackId ? buildScheduleTrackKey(targetTrackId) : DRAFT_CURRENT_TRACK_KEY);
            if (firstFlight?.id) {
                ui.setPendingFlightSelectionId(firstFlight.id);
            }
        },
    };
}

/**
 * Applies cross-cutting useEffect side effects for the editor workspace.
 *
 * These effects coordinate between UI state, editor data, and draft identity:
 * 1) Resets sourceTrack when the current value is no longer valid.
 * 2) Deferred item selection: waits for canvasModel to include a newly
 *    created/imported item before selecting it.
 * 3) Deselects when selectedItemId points to a removed item.
 *
 * @param {Object} params
 * @param {Object} params.ui - UI state (from useScheduleDraftEditorUiState)
 * @param {Object} params.data - Editor data (from useScheduleDraftEditorData)
 * @param {string} params.draftId - Current draft ID
 */
export function useScheduleDraftEditorSideEffects({ ui, data, draftId }) {
    const canvasItems = data.canvasModel?.items || EMPTY_CANVAS_ITEMS;
    const selectableFlightItemIds = data.selectableFlightItemIds || EMPTY_SELECTABLE_FLIGHT_ITEM_IDS;

    useEffect(() => {
        const validValues = new Set((data.sourceTrackOptions || []).map((o) => o.value));
        if (!validValues.size) return;
        if (!validValues.has(ui.sourceTrack)) {
            ui.setTimetableTab(data.sourceTrackOptions[0].value);
        }
    }, [data.sourceTrackOptions, ui.setTimetableTab, ui.sourceTrack]);

    useEffect(() => {
        if (!ui.pendingFlightSelectionId || !draftId) return;

        const nextId = `draft:${draftId}:${ui.pendingFlightSelectionId}`;
        const exists = canvasItems.some((item) => item.id === nextId) || selectableFlightItemIds.has(nextId);
        if (!exists) return;

        ui.setSelectedItemId(nextId);
        ui.clearPendingFlightSelection();
    }, [ui.clearPendingFlightSelection, canvasItems, draftId, ui.pendingFlightSelectionId, selectableFlightItemIds, ui.setSelectedItemId]);

    useEffect(() => {
        if (ui.selectedItemId && !data.selectedItem) {
            ui.setSelectedItemId(null);
        }
    }, [data.selectedItem, ui.setSelectedItemId, ui.selectedItemId]);
}
